import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import AddCustomerModal from './AddCustomerModal'; 
import CustomerDetailView from './CustomerDetailView'; 

const CustomerManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Modals ki states
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/customers?search=${search}&page=${page}`);
            if (res.data.success) {
                setCustomers(res.data.customers);
                setTotalPages(res.data.totalPages);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchCustomers();
        }, 500); 
        return () => clearTimeout(delayDebounceFn);
    }, [search, page]);

    return (
        <div style={styles.container}>
            {/* Top Bar: Search & Add Button */}
            <div style={styles.topBar}>
                <div style={styles.searchWrapper}>
                    <input 
                        type="text" 
                        placeholder="Search Name, CNIC, Phone or Ref No..." 
                        style={styles.searchInput}
                        value={search}
                        onChange={(e) => {setSearch(e.target.value); setPage(1);}}
                    />
                    {loading && <span style={styles.loaderText}>Searching...</span>}
                </div>
                <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>
                    + Add New Customer
                </button>
            </div>

            {/* Table Area */}
            <div style={styles.tableCard}>
                <table style={styles.table}>
                    <thead>
                        <tr style={styles.thead}>
                            <th>Ref No</th>
                            <th>Full Name</th>
                            <th>CNIC</th>
                            <th>Phone</th>
                            <th>City</th>
                            <th>Reference</th>
                            <th style={{textAlign: 'center'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.length > 0 ? customers.map(c => (
                            <tr key={c.id} style={styles.tr}>
                                <td style={styles.td}><span style={styles.refBadge}>{c.ref_no || 'N/A'}</span></td>
                                <td style={styles.td}>
                                    <div style={{fontWeight: '600', color: '#1e293b'}}>{c.full_name}</div>
                                    <div style={{fontSize: '11px', color: '#64748b'}}>{c.father_name}</div>
                                </td>
                                <td style={styles.td}>{c.cnic}</td>
                                <td style={styles.td}>{c.phone}</td>
                                <td style={styles.td}>{c.city_name || '---'}</td>
                                <td style={styles.td}>
                                    <div style={{fontSize: '13px'}}>{c.ref_name}</div>
                                    <div style={{fontSize: '10px', color: '#94a3b8'}}>{c.ref_code}</div>
                                </td>
                                <td style={{...styles.td, textAlign: 'center'}}>
                                    <button 
                                        style={styles.viewBtn} 
                                        onClick={() => setSelectedCustomer(c)}
                                    >
                                        👁️ View
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="7" style={{padding: '40px', textAlign: 'center', color: '#94a3b8'}}>
                                    No customers found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div style={styles.pagination}>
                <button 
                    disabled={page === 1} 
                    onClick={() => setPage(p => p - 1)} 
                    style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                >
                    Previous
                </button>
                <span style={styles.pageInfo}>Page <b>{page}</b> of <b>{totalPages}</b></span>
                <button 
                    disabled={page === totalPages} 
                    onClick={() => setPage(p => p + 1)} 
                    style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                >
                    Next
                </button>
            </div>

            {/* Modals */}
            {showAddModal && (
                <AddCustomerModal 
                    onClose={() => setShowAddModal(false)} 
                    onRefresh={fetchCustomers} 
                />
            )}

            {selectedCustomer && (
                <CustomerDetailView 
                    customer={selectedCustomer} 
                    onClose={() => setSelectedCustomer(null)} 
                    onCustomerUpdated={fetchCustomers} 
                />
            )}
        </div>
    );
};

const styles = {
    container: { animation: 'fadeIn 0.3s ease-in' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px' },
    searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchInput: { padding: '12px 15px', width: '400px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
    loaderText: { position: 'absolute', right: '15px', fontSize: '12px', color: '#3b82f6' },
    addBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' },
    tableCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    thead: { background: '#f8fafc', color: '#64748b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: '0.2s' },
    td: { padding: '14px 15px', fontSize: '14px', color: '#334155' },
    refBadge: { background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', color: '#475569' },
    viewBtn: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '13px', transition: '0.2s' },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '25px', gap: '15px' },
    pageBtn: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: '500' },
    pageBtnDisabled: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #f1f5f9', background: '#f8fafc', color: '#cbd5e1', cursor: 'not-allowed' },
    pageInfo: { fontSize: '14px', color: '#64748b' }
};

export default CustomerManagement;